<template lang="pug">
  v-card(outlined)
    v-card-title(v-if="endpoint.description").endpoint-title.mb-3
      | {{ endpoint.description }}
    div.ml-3.mb-2.mt-2
      v-chip.method-chip(
        :color="getMethodColor(endpoint.method)"
        dark
        mr-2
      ) {{ endpoint.method }}
      span.method-url {{ endpoint.url }}
    
    v-card-text
      template(v-if="endpoint.fields && endpoint.fields.length > 0")
        v-form
          template(v-for="field in endpoint.fields")
            //- Select dropdown for fields with items
            v-select(
              v-if="field.type === 'select' && field.items"
              :key="field.name"
              v-model="fieldData[field.name]"
              :items="field.items"
              :item-text="field.itemText || 'text'"
              :item-value="field.itemValue || 'value'"
              :placeholder="field.placeholder"
              :label="getFieldLabel(field)"
              outlined
              dense
              mb-2
            )
            //- Switch for boolean fields
            v-switch(
              v-else-if="field.type === 'switch' || field.type === 'boolean'"
              :key="field.name"
              v-model="fieldData[field.name]"
              mb-2
            )
              template(slot="label")
                span {{ field.label }}
                span.field-label-optional(v-if="!field.required")  (optional)
                span.field-label-required(v-else)  (required)
            //- Textarea for textarea fields
            v-textarea(
              v-else-if="field.type === 'textarea'"
              :key="field.name"
              v-model="fieldData[field.name]"
              :placeholder="field.placeholder"
              :label="getFieldLabel(field)"
              :rows="4"
              outlined
              dense
              mb-2
            )
            //- Number input for number fields
            v-text-field(
              v-else-if="field.type === 'number'"
              :key="field.name"
              v-model.number="fieldData[field.name]"
              :placeholder="field.placeholder"
              :label="getFieldLabel(field)"
              type="number"
              outlined
              dense
              mb-2
            )
            //- Default text input
            v-text-field(
              v-else
              :key="field.name"
              v-model="fieldData[field.name]"
              :placeholder="field.placeholder"
              :label="getFieldLabel(field)"
              :type="field.type === 'password' ? 'password' : 'text'"
              outlined
              dense
              mb-2
            )
    v-card-actions
      v-spacer
      v-btn.call-endpoint-btn(
        @click="handleCall"
        :loading="loading"
        :disabled="loading || !isUserIdFilled"
      ).my-2.mr-2 Run
    
    //- Response display card
    response-viewer(v-if="response" :response="response" mt-2)
</template>

<script>
import { mapActions } from 'vuex';
import ResponseViewer from './ResponseViewer';

export default {
  name: 'EndpointCard',
  components: {
    ResponseViewer,
  },
  props: {
    endpoint: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      fieldData: {},
      loading: false,
      response: null,
    };
  },
  created() {
    // Initialize field data
    if (this.endpoint.fields) {
      this.endpoint.fields.forEach(field => {
        // Set default values based on field type
        if (field.type === 'switch' || field.type === 'boolean') {
          this.$set(this.fieldData, field.name, false);
        } else if (field.type === 'number') {
          this.$set(this.fieldData, field.name, null);
        } else {
          this.$set(this.fieldData, field.name, '');
        }
      });
    }
  },
  computed: {
    isUserIdFilled() {
      // Find user_id field value
      const fields = this.endpoint && this.endpoint.fields ? this.endpoint.fields : [];
      const userIdField = fields.find(f => f.name === 'user_id');
      if (!userIdField) return true; // If no user_id field, allow the call
      
      const userIdValue = this.fieldData['user_id'];
      // Check if user_id has a valid value (not null, undefined, or empty string)
      return userIdValue !== null && userIdValue !== undefined && userIdValue !== '';
    },
  },
  methods: {
    ...mapActions('sunsetAdmin', [
      'callEndpoint',
    ]),
    getMethodColor(method) {
      const colors = {
        GET: 'blue',
        POST: 'green',
        PUT: 'orange',
        DELETE: 'red',
        PATCH: 'purple',
      };
      return colors[method] || 'grey';
    },
    getFieldLabel(field) {
      if (field.required) {
        return `${field.label} (required)`;
      }
      return `${field.label} (optional)`;
    },
    async handleCall() {
      console.log('[EndpointCard] handleCall triggered', {
        endpointId: this.endpoint.id,
        endpointUrl: this.endpoint.url,
        fieldData: this.fieldData,
      });

      this.loading = true;
      this.$emit('loading', true);

      try {
        // Build data object
        const data = {};
        if (this.endpoint.fields) {
          this.endpoint.fields.forEach(field => {
            const value = this.fieldData[field.name];
            // Handle required fields - always include them even if empty
            // Handle number fields - include if not null (0 is valid)
            if (field.type === 'number') {
              if (value !== null && value !== undefined && value !== '') {
                data[field.name] = Number(value);
              } else if (field.required) {
                // Required number field must have a value
                console.error('[EndpointCard] Required number field missing', { field: field.name, label: field.label });
                throw new Error(`Field "${field.label}" is required`);
              }
            } else if (value !== undefined && value !== null && value !== '') {
              // Try to parse JSON for textarea fields
              if (field.type === 'textarea') {
                try {
                  data[field.name] = JSON.parse(value);
                } catch {
                  data[field.name] = value;
                }
              } else if (field.type === 'switch' || field.type === 'boolean') {
                // Include boolean values explicitly
                data[field.name] = value === true || value === 'true';
              } else {
                data[field.name] = value;
              }
            } else if (field.type === 'switch' || field.type === 'boolean') {
              // Include false boolean values
              data[field.name] = false;
            } else if (field.required) {
              // Required field is missing
              console.error('[EndpointCard] Required field missing', { field: field.name, label: field.label });
              throw new Error(`Field "${field.label}" is required`);
            }
          });
        }

        console.log('[EndpointCard] Calling endpoint with data', {
          endpoint: this.endpoint,
          data,
        });

        const response = await this.callEndpoint({ endpoint: this.endpoint, data });
        
        console.log('[EndpointCard] Endpoint call successful', {
          status: response.status,
          data: response.data,
        });

        this.response = response;
        this.$emit('response', response);
      } catch (error) {
        console.error('[EndpointCard] Endpoint call failed', {
          error,
          errorMessage: error.message,
          errorStatus: error.status,
          errorData: error.data,
          stack: error.stack,
        });

        if (error.status) {
          this.response = error;
          this.$emit('response', error);
        } else {
          this.response = { status: 500, data: { error: error.message } };
          this.$emit('error', error.message);
        }
      } finally {
        this.loading = false;
        this.$emit('loading', false);
        console.log('[EndpointCard] handleCall completed', {
          endpointId: this.endpoint.id,
          hasResponse: !!this.response,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.method-url {
  font-family: monospace;
  font-size: 14px !important;
  color: #626262;
  margin-left: 8px;
  line-height: 18px !important;
}

.ml-3 {
  display: flex;
  align-items: center;
}

::v-deep .method-chip {
  font-family: -apple-system, "system-ui", "Segoe UI", Roboto, "Open Sans", Ubuntu, "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-size: 14px !important;
  font-weight: 700 !important;
  padding: 0px 12px !important;
  background-color: transparent !important;
  border: 1px solid #626262 !important;
  .v-chip__content {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: #626262 !important;
  }
}

.endpoint-title {
  font-family: -apple-system, "system-ui", "Segoe UI", Roboto, "Open Sans", Ubuntu, "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  font-weight: 700;
  font-size: 16px;
  background-color: rgb(94, 94, 94) !important;
  color: white !important;
  padding: 8px 16px !important;
}

::v-deep .call-endpoint-btn {
  background-color: rgb(94, 94, 94) !important;
  border-radius: 6px !important;
  padding: 12px 32px !important;
  color: white !important;
  box-shadow: none !important;
  transition: all 0.3s ease;
  height: 36px !important;
  
  .v-btn__content {
    font-family: -apple-system, "system-ui", "Segoe UI", Roboto, "Open Sans", Ubuntu, "Droid Sans", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    font-weight: 600 !important;
    font-size: 14px !important;
    text-transform: capitalize !important;
    color: white !important;
    line-height: 18px !important;
  }
  
  &:hover {
    background-color: rgb(94, 94, 94) !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
  }
  
  &:active {
    transform: translateY(0);
  }
}

// Style the switch label spans (for v-switch only, since it supports label slots)
::v-deep .field-label-optional {
  font-size: 11px !important;
  font-weight: 400;
  margin-left: 4px;
}

::v-deep .field-label-required {
  font-size: 12px !important;
  margin-left: 4px;
}
</style>

